import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';

// Route configuration for breadcrumb labels and hierarchy
const ROUTE_CONFIG = {
  // Inventory Module
  '/inventory': { label: 'Inventory', parent: '/' },
  '/inventory/items': { label: 'Item Master', parent: '/inventory' },
  '/inventory/items/new': { label: 'New Item', parent: '/inventory/items' },
  '/inventory/godowns': { label: 'Godowns', parent: '/inventory' },
  '/inventory/godowns/new': { label: 'New Godown', parent: '/inventory/godowns' },
  '/inventory/transactions': { label: 'Receive / Issue', parent: '/inventory' },
  '/inventory/transactions/new': { label: 'New Transaction', parent: '/inventory/transactions' },
  '/inventory/stock': { label: 'Stock View', parent: '/inventory' },
  '/inventory/stock-management': { label: 'Stock Management', parent: '/inventory' },
  '/inventory/job-work': { label: 'Job Work Tracker', parent: '/inventory' },
  '/inventory/low-stock': { label: 'Low Stock Report', parent: '/inventory' },
  '/inventory/categories': { label: 'Categories', parent: '/inventory' },
  '/inventory/pending-approvals': { label: 'Pending Approvals', parent: '/inventory' },
  '/inventory/work-orders': { label: 'Work Orders', parent: '/inventory' },
  '/inventory/routes': { label: 'Routes & BOM', parent: '/inventory' },
  '/inventory/routes/new': { label: 'New Route', parent: '/inventory/routes' },
  '/inventory/subcontract': { label: 'Subcontracting', parent: '/inventory' },
  '/inventory/customers': { label: 'Customer Master', parent: '/inventory' },
  '/inventory/customers/new': { label: 'New Customer', parent: '/inventory/customers' },
  
  // Purchase Module
  '/purchase': { label: 'Purchase', parent: '/' },
  '/purchase/suppliers': { label: 'Supplier Master', parent: '/purchase' },
  '/purchase/suppliers/new': { label: 'New Supplier', parent: '/purchase/suppliers' },
  '/purchase/indents': { label: 'Indents', parent: '/purchase' },
  '/purchase/indents/new': { label: 'New Indent', parent: '/purchase/indents' },
  '/purchase/requests': { label: 'Purchase Requests', parent: '/purchase' },
  '/purchase/requests/new': { label: 'New PR', parent: '/purchase/requests' },
  '/purchase/orders': { label: 'Purchase Orders', parent: '/purchase' },
  '/purchase/grn': { label: 'Goods Receipt', parent: '/purchase' },
  '/purchase/grn/new': { label: 'New GRN', parent: '/purchase/grn' },
  '/purchase/analytics': { label: 'Spend Analytics', parent: '/purchase' },
  
  // Production Module
  '/production': { label: 'Production', parent: '/' },
  '/production/work-centers': { label: 'Work Centers', parent: '/production' },
  '/production/routes': { label: 'Routes & BOM', parent: '/production' },
  '/production/work-orders': { label: 'Work Orders', parent: '/production' },
  '/production/subcontract': { label: 'Subcontract', parent: '/production' },
  '/production/qc': { label: 'Quality Check', parent: '/production' },
  '/production/capacity': { label: 'Capacity Planning', parent: '/production' },
  
  // Quality Module
  '/quality': { label: 'Quality', parent: '/' },
  '/quality/incoming-qc': { label: 'Incoming QC', parent: '/quality' },
  '/quality/mrb': { label: 'MRB', parent: '/quality' },
  '/quality/rejection-ppm': { label: 'Rejection PPM', parent: '/quality' },
  '/quality/complaints': { label: 'Customer Complaints', parent: '/quality' },
  '/quality/customer-scorecard': { label: 'Customer Scorecard', parent: '/quality' },
  '/quality/internal-ncr': { label: 'Internal NC Register', parent: '/quality' },
  '/quality/rejection-reasons': { label: 'Rejection Reasons', parent: '/quality' },
  '/quality/calibration': { label: 'Calibration', parent: '/quality' },
  '/quality/calibration-report': { label: 'Calibration Reports', parent: '/quality' },
  '/quality/audit-log': { label: 'Audit Log', parent: '/quality' },
  '/quality/ncr': { label: 'NCR Management', parent: '/quality' },
  '/quality/ncr/new': { label: 'New NCR', parent: '/quality/ncr' },
  
  // Calibration Module (standalone)
  '/calibration': { label: 'Calibration', parent: '/' },
  '/calibration/dashboard': { label: 'Dashboard', parent: '/calibration' },
  '/calibration/tools': { label: 'Instruments', parent: '/calibration' },
  '/calibration/tools/new': { label: 'New Instrument', parent: '/calibration/tools' },
  '/calibration/schedule': { label: 'Schedule', parent: '/calibration' },
  '/calibration/history': { label: 'History', parent: '/calibration' },
  '/calibration/certificates': { label: 'Certificates', parent: '/calibration' },
  
  // Suppliers Module
  '/suppliers': { label: 'Suppliers', parent: '/' },
  '/suppliers/list': { label: 'Supplier List', parent: '/suppliers' },
  '/suppliers/report': { label: 'Reports', parent: '/suppliers' },
  '/suppliers/new': { label: 'New Supplier', parent: '/suppliers' },
  
  // Contract Module
  '/contracts': { label: 'Contracts', parent: '/' },
  '/contracts/customers': { label: 'Customer Master', parent: '/contracts' },
  '/contracts/purchase-orders': { label: 'Purchase Orders', parent: '/contracts' },
  '/contracts/list': { label: 'All Contracts', parent: '/contracts' },
  '/contracts/new': { label: 'New Contract', parent: '/contracts' },
  
  // Dispatch Module
  '/dispatch': { label: 'Dispatch', parent: '/' },
  '/dispatch/challans': { label: 'Challans', parent: '/dispatch' },
  '/dispatch/challans/new': { label: 'New Challan', parent: '/dispatch/challans' },
  '/dispatch/invoices': { label: 'Invoices', parent: '/dispatch' },
  
  // Admin
  '/admin': { label: 'Admin', parent: '/' },
  '/admin/users': { label: 'Users', parent: '/admin' },
  '/admin/audit': { label: 'Audit Log', parent: '/admin' },
  
  // Root
  '/': { label: 'Home', parent: null },
};

// Get breadcrumb label for dynamic routes (e.g., /inventory/items/:id)
const getDynamicLabel = (pathname, segment) => {
  // Check for ID patterns (UUIDs, specific IDs)
  if (segment.match(/^[a-f0-9-]{36}$/i)) {
    return 'Details';
  }
  if (segment.match(/^[A-Z]+-\d+/i)) {
    return segment; // Return formatted IDs like WO-001, PR-2024-001
  }
  return segment;
};

// Build breadcrumb path from current location
const buildBreadcrumbs = (pathname) => {
  const breadcrumbs = [];
  const segments = pathname.split('/').filter(Boolean);
  
  let currentPath = '';
  
  for (let i = 0; i < segments.length; i++) {
    currentPath += '/' + segments[i];
    
    // Check if this is a known route
    const config = ROUTE_CONFIG[currentPath];
    
    if (config) {
      breadcrumbs.push({
        path: currentPath,
        label: config.label,
        isLast: i === segments.length - 1,
      });
    } else {
      // Handle dynamic routes (e.g., /inventory/items/:id)
      const parentPath = '/' + segments.slice(0, i).join('/');
      const parentConfig = ROUTE_CONFIG[parentPath];
      
      if (parentConfig) {
        breadcrumbs.push({
          path: currentPath,
          label: getDynamicLabel(pathname, segments[i]),
          isLast: i === segments.length - 1,
        });
      }
    }
  }
  
  return breadcrumbs;
};

// Get parent path for "Back to [Parent]" button
const getParentPath = (pathname) => {
  const config = ROUTE_CONFIG[pathname];
  if (config?.parent) {
    return { path: config.parent, label: ROUTE_CONFIG[config.parent]?.label || 'Back' };
  }
  
  // For dynamic routes, go up one level
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 1) {
    const parentPath = '/' + segments.slice(0, -1).join('/');
    const parentConfig = ROUTE_CONFIG[parentPath];
    return { path: parentPath, label: parentConfig?.label || 'Back' };
  }
  
  return null;
};

export function PageBreadcrumb({ 
  customItems = null, // Override automatic breadcrumbs with custom items
  currentPageLabel = null, // Override the current page label
  showBackButton = true, // Show "Back to [Parent]" button
  className = '',
}) {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Use custom items if provided, otherwise build from route
  const breadcrumbs = customItems || buildBreadcrumbs(pathname);
  
  // Override last item label if provided
  if (currentPageLabel && breadcrumbs.length > 0) {
    breadcrumbs[breadcrumbs.length - 1].label = currentPageLabel;
  }
  
  const parent = getParentPath(pathname);
  
  // Don't show breadcrumb on home page
  if (pathname === '/' || breadcrumbs.length === 0) {
    return null;
  }
  
  return (
    <div className={`flex items-center gap-3 mb-4 ${className}`} data-testid="page-breadcrumb">
      {/* Back to Parent Button */}
      {showBackButton && parent && (
        <Button 
          variant="ghost" 
          size="sm" 
          asChild 
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          data-testid="back-to-parent-btn"
        >
          <Link to={parent.path}>
            <ChevronLeft className="w-3.5 h-3.5 mr-0.5" />
            Back to {parent.label}
          </Link>
        </Button>
      )}
      
      {/* Separator */}
      {showBackButton && parent && (
        <span className="text-muted-foreground/50">|</span>
      )}
      
      {/* Breadcrumb Trail */}
      <Breadcrumb>
        <BreadcrumbList className="text-xs">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.path}>
              <BreadcrumbItem>
                {crumb.isLast ? (
                  <BreadcrumbPage className="font-medium text-foreground">
                    {crumb.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link 
                      to={crumb.path} 
                      className="hover:text-foreground transition-colors"
                      data-testid={`breadcrumb-${crumb.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {crumb.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!crumb.isLast && <BreadcrumbSeparator />}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}

// Hook to get breadcrumb data programmatically
export function useBreadcrumbs() {
  const location = useLocation();
  return {
    breadcrumbs: buildBreadcrumbs(location.pathname),
    parent: getParentPath(location.pathname),
  };
}

export default PageBreadcrumb;
